<template>
  <div class="rounded-bl-[20px] rounded-br-[20px] rounded-tr-[20px] bg-white p-8 md:mt-0">
    <!-- Title -->
    <h3 class="pb-4 font-headline text-h3 font-normal text-blue-darker">
      {{ title }}
    </h3>

    <!-- Snabba fakta - Special styling with green background and clip-path -->
    <div v-if="blockType === 'fakta'">
      <collapsible-content :is-collapsible="isCollapsible">
        <ul>
          <li
            v-for="(fact, index) in facts"
            :key="index"
            class="font-roboto mt-2 rounded-lg bg-green-dark px-5 py-2 text-[17px] font-bold leading-normal text-white"
            style="
              clip-path: polygon(0 0, 100% 0, 95% 100%, 0 100%);
              border-top-right-radius: 0;
              border-bottom-right-radius: 0;
            "
          >
            {{ fact }}
          </li>
        </ul>
      </collapsible-content>
    </div>

    <!-- Värderingsfrågor - Special styling with numbered questions -->
    <div v-else-if="blockType === 'värderingsfrågor'">
      <div class="relative mt-5 text-slate-darker">
        <collapsible-content :is-collapsible="isCollapsible">
          <p v-if="preamble" class="mb-4 font-bold">{{ preamble }}</p>
          <ul>
            <li v-for="(question, index) in questions" :key="index" class="mt-4 flex">
              <div class="mr-3 font-headline text-h3 text-blue-dark">
                <span>{{ index + 1 }}</span>
              </div>
              <p class="inline-flex items-center font-body text-slate-darker">{{ question }}</p>
            </li>
          </ul>
        </collapsible-content>
      </div>
    </div>

    <!-- Djurens rätt - Special styling with italic intro -->
    <div v-else-if="blockType === 'djurens rätt'">
      <collapsible-content :is-collapsible="isCollapsible">
        <p class="py-5 font-body italic text-blue-darker">
          Här berättar etologerna på Djurens Rätt vad de tänker är bäst för djuren. De är utbildade
          i djurs behov och beteenden.
        </p>
        <p v-if="preamble" class="mb-4 font-body text-slate-darker">
          {{ preamble }}
        </p>
        <div class="font-body text-slate-darker">
          <contentful-rich-text :document="body" />
        </div>
      </collapsible-content>
    </div>

    <!-- Källor (Sources) - Collapsible functionality -->
    <div v-else-if="blockType === 'källa'">
      <div class="relative mt-5 text-slate-darker">
        <collapsible-content :is-collapsible="isCollapsible">
          <ul>
            <li v-for="(source, index) in sources" :key="index" class="flex flex-nowrap">
              <div class="mr-1">
                <span>{{ index + 1 }}.</span>
              </div>
              <source-list-item :source="source" />
            </li>
          </ul>
        </collapsible-content>
      </div>
    </div>

    <!-- Generic text block - Standard rich text rendering -->
    <div v-else>
      <collapsible-content :is-collapsible="isCollapsible">
        <div class="font-body text-slate-darker">
          <contentful-rich-text :document="body" />
        </div>
      </collapsible-content>
    </div>
  </div>
</template>

<script setup lang="ts">
interface Props {
  textBlockData: any
}

const props = defineProps<Props>()
// Extract data from TextBlock
const title = computed(() => props.textBlockData?.fields?.rubrik || '')
const body = computed(() => props.textBlockData?.fields?.body)
const blockType = computed(() => {
  const types = props.textBlockData?.fields?.blockType || []
  return types[0] || 'generell'
})
const preamble = computed(() => props.textBlockData?.fields?.preamble || '')

// Extract facts from body content for "Snabba fakta"
const facts = computed(() => {
  if (blockType.value !== 'fakta' || !body.value?.content) return []

  const facts: string[] = []

  const extractFacts = (nodes: any[]) => {
    nodes.forEach((node) => {
      if (node.nodeType === 'unordered-list' && node.content) {
        node.content.forEach((listItem: any) => {
          if (listItem.nodeType === 'list-item' && listItem.content) {
            listItem.content.forEach((paragraph: any) => {
              if (paragraph.nodeType === 'paragraph' && paragraph.content) {
                paragraph.content.forEach((textNode: any) => {
                  if (textNode.nodeType === 'text' && textNode.value) {
                    facts.push(textNode.value.trim())
                  }
                })
              }
            })
          }
        })
      } else if (node.content) {
        extractFacts(node.content)
      }
    })
  }

  extractFacts(body.value.content)
  return facts
})

// Extract questions from body content for "Värderingsfrågor"
const questions = computed(() => {
  if (blockType.value !== 'värderingsfrågor' || !body.value?.content) return []

  const questions: string[] = []

  const extractQuestions = (nodes: any[]) => {
    nodes.forEach((node) => {
      if (node.nodeType === 'unordered-list' && node.content) {
        node.content.forEach((listItem: any) => {
          if (listItem.nodeType === 'list-item' && listItem.content) {
            listItem.content.forEach((paragraph: any) => {
              if (paragraph.nodeType === 'paragraph' && paragraph.content) {
                paragraph.content.forEach((textNode: any) => {
                  if (textNode.nodeType === 'text' && textNode.value) {
                    questions.push(textNode.value.trim())
                  }
                })
              }
            })
          }
        })
      } else if (node.content) {
        extractQuestions(node.content)
      }
    })
  }

  extractQuestions(body.value.content)
  return questions
})

// Extract sources from body content for "Källor"
const sources = computed(() => {
  if (blockType.value !== 'källa' || !body.value?.content) return []

  const sources: Array<{ sourceName: string; url?: string }> = []

  const extractSources = (nodes: any[]) => {
    nodes.forEach((node) => {
      if (node.nodeType === 'unordered-list' && Array.isArray(node.content)) {
        node.content.forEach((listItem: any) => {
          if (listItem.nodeType !== 'list-item' || !Array.isArray(listItem.content)) return

          // Build a single source per list item
          let accumulatedText = ''
          let firstUrl: string | undefined

          listItem.content.forEach((paragraph: any) => {
            if (paragraph.nodeType !== 'paragraph' || !Array.isArray(paragraph.content)) return

            paragraph.content.forEach((child: any) => {
              if (child.nodeType === 'text' && child.value) {
                accumulatedText += child.value
              } else if (child.nodeType === 'hyperlink') {
                // Do not include hyperlink visible text in the source name; only capture URL
                if (!firstUrl && child?.data?.uri) {
                  firstUrl = child.data.uri
                }
              }
            })
          })

          // Remove empty parentheses left by omitted hyperlink text, collapse spaces
          const name = (accumulatedText || '')
            .replace(/\s*\(\s*\)\s*/g, ' ')
            .replace(/\s{2,}/g, ' ')
            .trim()
          if (name) {
            sources.push({ sourceName: name, url: firstUrl })
          }
        })
      } else if (Array.isArray(node.content)) {
        extractSources(node.content)
      }
    })
  }

  extractSources(body.value.content)
  return sources
})

// Collapsible functionality
const isCollapsible = computed(() => {
  // Check if the text block has the collapsible field set to true
  if (props.textBlockData?.fields?.collapsible === true) {
    return true
  }

  return false
})
</script>
